import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/axios';
import { useAuth } from '../context/AuthContext';
import styles from './Auth.module.css';

export const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { getHomeForRole, passwordChangeRequired, setPasswordChangeRequired, setUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/api/user/change-password', {
        ...(passwordChangeRequired ? {} : { current_password: currentPassword }),
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
      });
      
      const userRes = await api.get('/api/auth/me');
      const updatedUser = userRes.data.user;
      setUser(updatedUser);
      setPasswordChangeRequired(userRes.data.password_change_required || false);
      
      const homePath = getHomeForRole(updatedUser?.role);
      navigate(homePath, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to change password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.card}>
        <div className={styles.logoSection}>
          <div className={styles.logoIcon}>🔒</div>
          <h1 className={styles.logoTitle}>Change Password</h1>
          <p className={styles.logoSubtitle}>For security reasons, you must change your password before continuing.</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {!passwordChangeRequired && (
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Current Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                autoFocus
                className={styles.formInput}
                placeholder="Enter current password"
              />
            </div>
          )}

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>New Password (min. 8 characters)</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              className={styles.formInput}
              placeholder="Enter new password"
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              className={styles.formInput}
              placeholder="Confirm new password"
            />
          </div>

          {error && (
            <div className={styles.errorMessage}>
              ⚠️ {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className={styles.primaryButton}
          >
            {isSubmitting ? 'Changing Password...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
};
